import { OptionCard } from './OptionCard';
import type { PropScoringQuestionProps } from '@/utils/scoring/adapters/propScoringAdapter';
import type { MatchStatsResponse } from '@/api/feed/getFeedMatchStats';

interface PropScoringQuestionComponentProps extends PropScoringQuestionProps {
  questionId?: string;
  fixtureId?: string;
  onMarkCorrect?: (optionId: string, isCorrect: boolean) => void;
  onSave?: (optionId: string) => void;
  onCancel?: (optionId: string) => void;
  language?: 'en' | 'fr';
  manualScoring?: boolean;
  matchStats?: MatchStatsResponse;
  onStatChange?: (optionId: string, statValue: number) => void;
  onOverrideSuccess?: () => void;
}

export function PropScoringQuestion({
  questionId,
  fixtureId,
  statTypeCode,
  options,
  onMarkCorrect,
  onSave,
  onCancel,
  language = 'en',
  manualScoring = false,
  matchStats,
  onStatChange,
  onOverrideSuccess,
}: PropScoringQuestionComponentProps) {
  // For prop questions with per-option scoring, use vertical grid layout
  // Check if this is a per-option question (multiple instances of same player)
  // This happens when bonus_type is 'per_option' and we have multiple selections
  const isPerOption =
    options.length > 4 && options.every(opt => opt.option_text_en === options[0].option_text_en);

  if (isPerOption) {
    // Vertical grid layout for per-option scoring (like "Select a Player that will have the most RUS TDs")
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-4 gap-3">
          {options.map((option, index) => (
            <OptionCard
              key={option.option_id || index}
              questionId={questionId}
              fixtureId={fixtureId}
              statTypeCode={statTypeCode}
              option={option}
              language={language}
              manualScoring={manualScoring}
              matchStats={matchStats}
              onMarkCorrect={onMarkCorrect}
              onStatChange={onStatChange}
              onSave={onSave}
              onCancel={onCancel}
              onOverrideSuccess={onOverrideSuccess}
              variant="vertical"
              showActions={false}
            />
          ))}
        </div>
      </div>
    );
  }

  // Horizontal layout for regular prop questions
  return (
    <div className="space-y-3">
      {/* TEMPORARY LABEL */}
      <div className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">
        PROP SCORING
      </div>
      <div className="flex flex-col gap-2">
        {options.map((option, index) => (
          <OptionCard
            key={option.option_id || index}
            questionId={questionId}
            fixtureId={fixtureId}
            statTypeCode={statTypeCode}
            option={option}
            language={language}
            manualScoring={manualScoring}
            matchStats={matchStats}
            onMarkCorrect={onMarkCorrect}
            onStatChange={onStatChange}
            onSave={onSave}
            onCancel={onCancel}
            onOverrideSuccess={onOverrideSuccess}
            variant="horizontal"
            showActions={true}
          />
        ))}
      </div>
    </div>
  );
}
